import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';

const FormInputs = ({ formData, handleInputChange, stateOptions }) => {
  return (
    <div className="form-section">
      <style>
        {`
          .form-section {
            background: rgba(17, 24, 39, 0.95);
            border-radius: 16px;
            padding: 24px;
            width: 100%;
            max-width: 800px;
            margin: 0 auto;
          }

          .form-grid {
            display: flex;
            flex-direction: column;
            gap: 20px;
            width: 100%;
          }

          .input-group {
            position: relative;
            width: 100%;
          }

          .input-label {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-bottom: 8px;
            color: #e2e8f0;
            font-size: 0.95rem;
            font-weight: 500;
          }

          .required-asterisk {
            color: #ff6b6b;
            font-size: 0.85rem;
            margin-left: 4px;
            opacity: 0.85;
            position: relative;
            top: -1px;
          }

          .info-icon {
            color: #64748b;
            font-size: 14px;
            cursor: help;
            margin-left: 6px;
            opacity: 0.7;
            transition: opacity 0.2s ease;
          }

          .info-icon:hover {
            opacity: 1;
            color: #60a5fa;
          }

          .input-wrapper {
            position: relative;
            width: 100%;
          }

          .currency-input,
          .state-select {
            width: 100%;
            height: 42px;
            padding: 8px 16px;
            background: rgba(17, 24, 39, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            color: #fff;
            font-size: 0.95rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .currency-input {
            padding-left: 32px;
          }

          .currency-symbol {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #64748b;
            font-size: 0.95rem;
            pointer-events: none;
          }

          .currency-input:focus,
          .state-select:focus {
            outline: none;
            border-color: #60a5fa;
            background: rgba(17, 24, 39, 0.8);
          }

          .currency-input::placeholder {
            color: #64748b;
          }

          .state-select {
            appearance: none;
            padding-right: 36px;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 16px;
          }

          .state-select option {
            background: #1e293b;
            color: #fff;
            padding: 8px;
          }

          .tooltip {
            position: relative;
            display: inline-flex;
          }

          .tooltip-text {
            position: absolute;
            top: -4px;
            left: 24px;
            background: #1e293b;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 0.875rem;
            color: #e2e8f0;
            width: max-content;
            max-width: 250px;
            opacity: 0;
            visibility: hidden;
            transform: translateY(-4px);
            transition: all 0.2s ease;
            z-index: 10;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .tooltip:hover .tooltip-text {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
          }
        `}
      </style>

      <div className="form-grid">
        <div className="input-group">
          <label className="input-label">
            Expected Annual Revenue
            <span className="required-asterisk">*</span>
            <div className="tooltip">
              <FaInfoCircle className="info-icon" />
              <span className="tooltip-text">
                Projected yearly revenue for your organization
              </span>
            </div>
          </label>
          <div className="input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text"
              className="currency-input"
              value={formData.expectedRevenue}
              onChange={handleInputChange}
              name="expectedRevenue"
              placeholder=""
              required
            />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">
            Primary State of Operation
            <span className="required-asterisk">*</span>
            <div className="tooltip">
              <FaInfoCircle className="info-icon" />
              <span className="tooltip-text">
                State where your organization primarily operates
              </span>
            </div>
          </label>
          <select
            className="state-select"
            value={formData.state}
            onChange={handleInputChange}
            name="state"
            required
          >
            {stateOptions.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label className="input-label">
            Estimated Total Assets
            <div className="tooltip">
              <FaInfoCircle className="info-icon" />
              <span className="tooltip-text">
                Used to determine Form 990-EZ eligibility
              </span>
            </div>
          </label>
          <div className="input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text"
              className="currency-input"
              value={formData.totalAssets}
              onChange={handleInputChange}
              name="totalAssets"
              placeholder="For EZ eligibility"
            />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">
            Annual Grant Funding
            <div className="tooltip">
              <FaInfoCircle className="info-icon" />
              <span className="tooltip-text">
                Used to determine audit requirements
              </span>
            </div>
          </label>
          <div className="input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text"
              className="currency-input"
              value={formData.grantFunding}
              onChange={handleInputChange}
              name="grantFunding"
              placeholder="For Audit check"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormInputs; 